/**
 * Playbooks, and what one would actually yield against each configuration item.
 *
 * The second card is the point of the screen. A playbook states what a procedure *asks for*; the
 * CI's ceiling states what the client has agreed to; the session gets the intersection. Showing
 * that before an access request exists is what stops an engineer discovering at approval time that
 * three of their four targets were dropped — by which point somebody's approval has been spent.
 *
 * `usable: false` is shown rather than filtered out. A CI with a target but no capability is not a
 * bug: most CIs are records, not doors, and seeing which is which is the useful part.
 */

import { useState, type JSX } from 'react';

import { api, type PlaybookMatch } from '../shared/api/client';
import {
  Badge,
  Card,
  ErrorBox,
  Formula,
  PageHeader,
  Spinner,
  Stat,
  StatRow,
  Table,
  type Tone,
} from '../shared/ui/ui';
import { useAsync } from '../shared/ui/useAsync';

/** `remediate` is toned as a caution: it is the profile that can change something. */
function profileTone(profile: string): Tone {
  if (profile === 'remediate') return 'warn';
  if (profile === 'view_only') return 'muted';
  return 'info';
}

export function Playbooks(): JSX.Element {
  const { data, error, loading } = useAsync(() => api.playbooks());
  const tickets = useAsync(() => api.tickets(50));

  const [selected, setSelected] = useState<string | null>(null);
  const match = useAsync<PlaybookMatch | null>(
    () => (selected === null ? Promise.resolve(null) : api.playbookForTicket(selected)),
    [selected],
  );

  const playbooks = data ?? [];
  const items = tickets.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Playbooks"
        subtitle="Declarative procedures. A playbook states what it asks for; the CI's ceiling decides what it gets."
      />

      <ErrorBox error={error} />
      <ErrorBox error={tickets.error} />
      <ErrorBox error={match.error} />

      <Card>
        <StatRow>
          <Stat label="Loaded" value={playbooks.length} hint="from files, at startup" />
          <Stat
            label="Can change something"
            value={playbooks.filter((p) => p.profile === 'remediate').length}
            tone={playbooks.some((p) => p.profile === 'remediate') ? 'warn' : 'neutral'}
            hint="profile = remediate"
          />
          <Stat
            label="Demand dual approval"
            value={playbooks.filter((p) => p.requires_dual_approval).length}
          />
        </StatRow>
      </Card>

      <Card
        title="Procedures"
        subtitle="Files, not rows — changing one goes through review and deployment, because the access block is policy. No playbook can express a command."
      >
        {loading ? (
          <Spinner />
        ) : (
          <Table
            columns={['Playbook', 'Matches', 'Asks for', 'Steps']}
            empty="No playbooks loaded. Every ticket is worked without a matched procedure, which is a valid deployment."
          >
            {playbooks.map((p) => (
              <tr key={p.id}>
                <td>
                  <div className="table__strong">{p.name}</div>
                  <Formula>
                    {p.id} v{p.version}
                  </Formula>
                </td>
                <td>
                  <Formula>
                    {[p.matches_kind, p.matches_domain].filter(Boolean).join(' · ')}
                    {p.matches_signals.length > 0 && ` · ${p.matches_signals.join(', ')}`}
                  </Formula>
                </td>
                <td>
                  <Badge tone={profileTone(p.profile)}>{p.profile.replace('_', ' ')}</Badge>
                  <Formula>
                    {p.max_duration_minutes}m · {p.recording.replace(/_/g, ' ')}
                    {p.requires_dual_approval && ' · dual approval'}
                  </Formula>
                </td>
                <td>
                  <Formula>{p.steps.join(' → ')}</Formula>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card
        title="What would this ticket get?"
        subtitle="The match, and the playbook's request intersected with each CI's ceiling — answered before an access request exists."
      >
        <div className="inline-form">
          <select
            className="input"
            aria-label="Ticket"
            value={selected ?? ''}
            onChange={(e) => {
              setSelected(e.target.value === '' ? null : e.target.value);
            }}
          >
            <option value="">Pick a ticket…</option>
            {items.map((t) => (
              <option key={t.id} value={t.id}>
                {t.title}
              </option>
            ))}
          </select>
        </div>

        {selected !== null && match.loading && <Spinner />}

        {match.data !== null && match.data.playbook === null && (
          <div className="alert">
            <strong>No playbook matched.</strong> {match.data.no_match_reason}
          </div>
        )}

        {match.data?.playbook != null && (
          <>
            <Formula>
              matched {match.data.playbook.id} v{match.data.playbook.version} ·{' '}
              {match.data.playbook.profile} · {match.data.playbook.max_duration_minutes}m
            </Formula>
            <Table
              columns={['Configuration item', 'Usable', 'Targets', 'Dropped']}
              empty="No configuration items in this tenant."
            >
              {match.data.derived.map((d) => (
                <tr key={d.ci_id}>
                  <td className="table__strong">{d.ci_name}</td>
                  <td>
                    {d.usable ? (
                      <Badge tone="ok">yes</Badge>
                    ) : (
                      /* Not a fault. Most CIs are records rather than doors, and the ceiling
                         saying so is the control working. */
                      <Badge tone="muted">record only</Badge>
                    )}
                  </td>
                  <td>
                    <Formula>{d.targets.length === 0 ? '—' : d.targets.join(', ')}</Formula>
                  </td>
                  <td>
                    {/* Surfaced rather than silently discarded: a consistently dropped target
                        usually means the CMDB is wrong, not the playbook. */}
                    <Formula>
                      {d.dropped_targets.length === 0 ? '—' : d.dropped_targets.join(', ')}
                    </Formula>
                  </td>
                </tr>
              ))}
            </Table>
          </>
        )}
      </Card>
    </>
  );
}
